import {
  CAlert,
  CButton,
  CCard,
  CCardBody,
  CFormInput,
  CFormLabel,
  CFormTextarea,
  CSpinner,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import {
  cilCheck,
  cilCheckCircle,
  cilCloudUpload,
  cilFile,
  cilShieldAlt,
  cilUser,
  cilWarning,
  cilX,
} from "@coreui/icons";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  getPublicTranslationAction,
  submitPublicTranslationAction,
} from "src/api/commonapi";
import { Routes } from "src/constants/api_url";
import { notification } from "src/constants/message";
import CustomImage from "src/views/CommonFunctions/CustomImage";
import "../Lead/ProformaRequestForm.css";
import "./TranslationActionForm.css";

const MAX_FILE_SIZE = 25 * 1024 * 1024;

const Icons = {
  check: cilCheck,
  checkCircle: cilCheckCircle,
  upload: cilCloudUpload,
  file: cilFile,
  shield: cilShieldAlt,
  user: cilUser,
  warning: cilWarning,
  x: cilX,
};

const MODE_META = {
  confirm: {
    icon: "upload",
    tone: "blue",
    title: "Upload translated document",
    subtitle: "Attach the final translated files for this request.",
  },
  confirmed: {
    icon: "checkCircle",
    tone: "green",
    title: "Confirm translated documents",
    subtitle: "Please verify that the translated documents are correct.",
  },
  correction: {
    icon: "warning",
    tone: "amber",
    title: "Request a correction",
    subtitle:
      "Describe what needs to be corrected so our team can update the documents.",
  },
};

const MODE_ACTIONS = {
  confirm: {
    label: "Submit Confirmation",
    color: "primary",
    className: "pf-submit",
  },
  confirmed: {
    label: "Confirm Documents",
    color: "success",
    className: "pf-submit ta-submit-success",
  },
  correction: {
    label: "Submit Correction",
    color: "danger",
    className: "pf-submit ta-submit-danger",
  },
};

const SectionHeader = ({ icon, tone, title, subtitle }) => (
  <div className="pf-section-head d-flex align-items-start gap-3 mb-3">
    <span
      className={`pf-icon pf-icon-${tone} d-flex align-items-center justify-content-center flex-shrink-0`}
    >
      <CIcon icon={Icons[icon]} />
    </span>
    <div>
      <h2 className="h6 fw-bold mb-0">{title}</h2>
      {subtitle ? <p className="mb-0 mt-1">{subtitle}</p> : null}
    </div>
  </div>
);

const renderLogo = () => (
  <div className="pf-logo bg-white rounded-circle overflow-hidden d-flex align-items-center justify-content-center flex-shrink-0">
    <CustomImage
      src={`${Routes.API_HOSTNAME}/images/adminlogo/loginlogo.png`}
      width="100%"
      height="100%"
      style={{ objectFit: "contain" }}
    />
  </div>
);

const renderTrust = () => (
  <div className="pf-trust d-inline-flex align-items-center gap-2 flex-shrink-0 rounded-pill fw-semibold align-self-start align-self-md-center">
    <CIcon icon={Icons.shield} />
    <span>{"Secure \u2022 Fast \u2022 Reliable"}</span>
  </div>
);

const renderHero = (title, subtitle) => (
  <CCard className="pf-hero border-0 text-white">
    <CCardBody className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3 p-4">
      <div className="d-flex align-items-center gap-3" style={{ minWidth: 0 }}>
        {renderLogo()}
        <div>
          <h1 className="fw-bold mb-0">{title}</h1>
          {subtitle ? <p className="mb-0 mt-1">{subtitle}</p> : null}
        </div>
      </div>
      {renderTrust()}
    </CCardBody>
  </CCard>
);

const PageShell = ({ heroTitle, heroSubtitle, children }) => (
  <div className="pf-page min-vh-100 py-3 py-md-4 px-2 px-md-3 pb-4 pb-md-5">
    <div className="mx-auto w-100" style={{ maxWidth: "980px" }}>
      {renderHero(heroTitle, heroSubtitle)}
      {children}
    </div>
  </div>
);

const StatusCard = ({ tone, icon, title, message }) => (
  <CCard className="pf-card mt-3">
    <CCardBody className="pf-success ta-status text-center">
      <div
        className={`ta-state-icon ta-state-icon-${tone} d-flex align-items-center justify-content-center rounded-circle mx-auto mb-3`}
      >
        <CIcon icon={icon} />
      </div>
      <h2 className="fw-bold mb-0">{title}</h2>
      {message ? (
        <p className="text-body mt-2 mb-0" style={{ fontSize: "14px" }}>
          {message}
        </p>
      ) : null}
    </CCardBody>
  </CCard>
);

const TranslationActionForm = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const code = queryParams.get("code");

  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState("");
  const [data, setData] = useState(null);
  const [files, setFiles] = useState([]);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitResponse, setSubmitResponse] = useState(0);
  const [successMessage, setSuccessMessage] = useState("");

  const statusId = Number(data?.status_id);
  const mode =
    statusId === 1061
      ? "confirm"
      : statusId === 1064
      ? "confirmed"
      : statusId === 1130
      ? "correction"
      : "";

  useEffect(() => {
    const loadData = async () => {
      if (!code) {
        setPageError("This link is invalid.");
        setLoading(false);
        return;
      }

      try {
        const resp = await getPublicTranslationAction(code);
        if (resp?.status == 0) {
          setPageError(resp?.message || "This link is invalid.");
        } else {
          setData(resp?.data);
        }
      } catch (error) {
        setPageError("Unable to load this link. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const selectFiles = (event) => {
    const selected = Array.from(event.target.files || []);
    const invalid = selected.find((file) => file.size > MAX_FILE_SIZE);
    if (invalid) {
      notification({
        type: "error",
        message: `${invalid.name} is larger than 25 MB.`,
      })();
      event.target.value = "";
      return;
    }

    setFiles((prev) => {
      const merged = [...prev];
      selected.forEach((file) => {
        const exists = merged.some(
          (item) =>
            item.name === file.name &&
            item.size === file.size &&
            item.lastModified === file.lastModified
        );
        if (!exists) merged.push(file);
      });
      return merged;
    });
    event.target.value = "";
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, fileIndex) => fileIndex !== index));
  };

  const submitAction = async () => {
    if (mode === "confirm" && files.length === 0) {
      notification({
        type: "error",
        message: "Please upload the translated document.",
      })();
      return;
    }

    if (mode === "correction" && !reason.trim()) {
      notification({
        type: "error",
        message: "Please enter the correction details.",
      })();
      return;
    }

    setSubmitting(true);
    const formdata = new FormData();
    formdata.append("code", code);
    if (mode === "correction") {
      formdata.append("reason", reason.trim());
    }
    if (mode === "confirm") {
      files.forEach((file) => formdata.append("files", file));
    }

    try {
      const resp = await submitPublicTranslationAction(formdata);
      notification({
        type: resp?.status == 0 ? "error" : "success",
        message: resp?.message || "Unable to submit your response.",
      })();
      if (resp?.status == 1) {
        setSuccessMessage(resp?.message || "");
        setSubmitResponse(1);
      }
    } catch (error) {
      notification({
        type: "error",
        message: "Something went wrong. Please try again.",
      })();
    } finally {
      setSubmitting(false);
    }
  };

  const renderDocumentTable = () => (
    <CCard className="ta-table-card">
      <div className="table-responsive">
        <table className="table ta-table align-middle mb-0">
          <thead>
            <tr>
              <th>#</th>
              <th>Applicant</th>
              <th>Type</th>
              <th>Country</th>
              <th>Document</th>
              <th>Tracking ID</th>
            </tr>
          </thead>
          <tbody>
            {data?.items?.map((item, index) => (
              <tr key={item.detail_id}>
                <td className="ta-col-num">{index + 1}</td>
                <td>
                  {[item.item_prefix, item.item_name].filter(Boolean).join(" ")}
                </td>
                <td>{item.type_name || "-"}</td>
                <td>{item.country_name || "-"}</td>
                <td>{item.certificate_name || "-"}</td>
                <td className="ta-tracking">{item.d_tracking_id || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </CCard>
  );

  const renderActionForm = () => {
    if (mode === "confirm") {
      return (
        <>
          <div className="mb-3">
            <CFormLabel className="pf-label">
              Upload the translated document{" "}
              <span className="text-danger">*</span>
            </CFormLabel>
            <CFormInput
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
              className="pf-file-input"
              onChange={selectFiles}
            />
            <p className="ta-hint mb-0">
              Accepted formats: PDF, JPG, PNG, GIF, WEBP. Maximum 25 MB per
              file.
            </p>
          </div>

          {files.length > 0 && (
            <div className="mb-3">
              <div className="ta-file-list">
                {files.map((file, index) => (
                  <div
                    key={`${file.name}_${index}`}
                    className="ta-file-chip"
                  >
                    <span className="ta-file-icon">
                      <CIcon icon={Icons.file} />
                    </span>
                    <span className="ta-file-name">{file.name}</span>
                    <button
                      type="button"
                      className="ta-file-remove"
                      onClick={() => removeFile(index)}
                      aria-label={`Remove ${file.name}`}
                    >
                      <CIcon icon={Icons.x} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      );
    }

    if (mode === "confirmed") {
      return (
        <CAlert
          color="success"
          className="pf-alert d-flex align-items-center gap-2 py-2 px-3 mb-0"
          style={{
            backgroundColor: "#e9f8f0",
            borderColor: "#7cb89b",
            color: "#177a4c",
            fontSize: "12.5px",
            fontWeight: 500,
          }}
        >
          <CIcon icon={Icons.check} style={{ width: 14, height: 14 }} />
          <span>Please confirm that the translated documents are correct.</span>
        </CAlert>
      );
    }

    if (mode === "correction") {
      return (
        <div className="mb-3">
          <CFormLabel className="pf-label">
            Correction details <span className="text-danger">*</span>
          </CFormLabel>
          <CFormTextarea
            rows={4}
            maxLength={300}
            className="pf-input"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Describe what needs to be corrected"
          />
          <span className="ta-counter">{reason.length}/300</span>
        </div>
      );
    }

    return (
      <CAlert
        color="info"
        className="pf-alert mb-0"
        style={{
          backgroundColor: "#eef7ff",
          borderColor: "#5b9bd5",
          color: "#000000",
          fontSize: "13px",
        }}
      >
        No action is required at this time.
      </CAlert>
    );
  };

  if (loading) {
    return (
      <PageShell
        heroTitle="Translation Document Confirmation"
        heroSubtitle="Loading your request details..."
      >
        <CCard className="pf-card mt-3">
          <CCardBody className="pf-success text-center">
            <CSpinner color="primary" />
            <p className="text-muted mt-3 mb-0" style={{ fontSize: "13px" }}>
              Please wait while we fetch your documents.
            </p>
          </CCardBody>
        </CCard>
      </PageShell>
    );
  }

  if (submitResponse == 1) {
    return (
      <PageShell
        heroTitle="Response submitted"
        heroSubtitle="Thank you for completing the requested action."
      >
        <StatusCard
          tone="success"
          icon={Icons.check}
          title="Thank You"
          message={successMessage || "Your response has been submitted."}
        />
      </PageShell>
    );
  }

  if (pageError) {
    return (
      <PageShell
        heroTitle="Translation Document Confirmation"
        heroSubtitle="We could not open this link."
      >
        <StatusCard
          tone="error"
          icon={Icons.warning}
          title="Link unavailable"
          message={pageError}
        />
      </PageShell>
    );
  }

  if (data?.completed) {
    return (
      <PageShell
        heroTitle="Response already submitted"
        heroSubtitle="No further action is required."
      >
        <StatusCard
          tone="success"
          icon={Icons.check}
          title="Thank You"
          message="This action has already been completed."
        />
      </PageShell>
    );
  }

  const customerName = [
    data?.customer?.c_prefix,
    data?.customer?.c_name,
  ]
    .filter(Boolean)
    .join(" ");
  const meta = MODE_META[mode];
  const action = MODE_ACTIONS[mode];

  return (
    <PageShell
      heroTitle="Translation Document Confirmation"
      heroSubtitle={
        customerName
          ? `Customer: ${customerName}`
          : "Review the documents below and complete the requested action."
      }
    >
      <CCard className="pf-card mt-3">
        <CCardBody className="p-3 p-md-4">
          <section className="pf-section">
            <SectionHeader
              icon="file"
              tone="purple"
              title="Documents"
              subtitle="Please review the documents linked to this request."
            />
            {data?.items?.length ? (
              renderDocumentTable()
            ) : (
              <CAlert
                color="info"
                className="pf-alert mb-0"
                style={{
                  backgroundColor: "#eef7ff",
                  borderColor: "#5b9bd5",
                  color: "#000000",
                  fontSize: "13px",
                }}
              >
                No documents are linked to this request.
              </CAlert>
            )}
          </section>

          <section className="pf-section">
            {meta ? (
              <SectionHeader
                icon={meta.icon}
                tone={meta.tone}
                title={meta.title}
                subtitle={meta.subtitle}
              />
            ) : null}
            {renderActionForm()}
          </section>

          <div className="d-flex flex-column flex-md-row align-items-stretch align-items-md-center justify-content-between gap-3 pt-4">
            {action ? (
              <CButton
                color={action.color}
                className={action.className}
                disabled={submitting}
                onClick={submitAction}
              >
                {submitting ? "Submitting..." : action.label}
              </CButton>
            ) : (
              <span />
            )}
            <div
              className="d-flex align-items-start gap-2 text-muted"
              style={{ fontSize: "12px" }}
            >
              <span className="pf-help-icon d-flex align-items-center justify-content-center flex-shrink-0 rounded-circle">
                <CIcon icon={Icons.shield} style={{ width: 16, height: 16 }} />
              </span>
              <div>
                <strong
                  className="d-block text-body"
                  style={{ fontSize: "12.5px" }}
                >
                  Need help?
                </strong>
                <p className="mb-0" style={{ lineHeight: 1.4 }}>
                  Our support team is here for you.
                </p>
              </div>
            </div>
          </div>
        </CCardBody>
      </CCard>
    </PageShell>
  );
};

export default TranslationActionForm;
